import React, { useState, useEffect, useRef } from 'react';
import { PREDEFINED_MESSAGES, CHAT_EMOJIS } from '../../utils/chatMessages';
import './GameChat.css';

interface ChatMessage {
  message: string;
  playerName: string;
  playerColor: 'white' | 'black' | null;
  timestamp: number;
  isOwn: boolean;
}

interface GameChatProps {
  gameId: string | null;
  currentPlayerName: string;
  currentPlayerColor: 'white' | 'black' | null;
  socket: any;
  isOnline: boolean;
  isMobile?: boolean;
  soundEnabled?: boolean;
  playChatSound?: (soundName: 'sendMsg' | 'rcvMsg') => void;
}

const MAX_MESSAGE_LENGTH = 150;

export const GameChat: React.FC<GameChatProps> = ({
  gameId,
  currentPlayerName,
  currentPlayerColor,
  socket,
  isOnline,
  isMobile = false,
  soundEnabled = true,
  playChatSound
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMessageMenu, setShowMessageMenu] = useState(false);
  const [showMobileMessageMenu, setShowMobileMessageMenu] = useState(false);
  const [showMobileQuickSend, setShowMobileQuickSend] = useState(false); // Track quick send expansion in mobile modal
  const [unreadCount, setUnreadCount] = useState(0);
  const [inputMessage, setInputMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const mobileMessagesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Determine if user is a spectator (currentPlayerColor === null means spectator)
  const isSpectator = currentPlayerColor === null;

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMessageMenu(false);
      }
    };

    if (showMessageMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMessageMenu]);

  // Listen for chat messages
  useEffect(() => {
    if (!socket || !isOnline || !gameId || !socket.connected) return;

    const handleChatMessage = (data: {
      gameId: string;
      message: string;
      playerName: string;
      playerColor: 'white' | 'black' | null;
      timestamp: number;
    }) => {
      if (data.gameId !== gameId) return;

      // Compare by playerColor instead of playerName for more reliable identification
      const isOwn = currentPlayerColor !== null && data.playerColor === currentPlayerColor;
      const newMessage: ChatMessage = {
        ...data,
        isOwn
      };

      setMessages(prev => [...prev, newMessage]);
      
      // Play receive message sound (only for messages from opponent)
      if (!isOwn && soundEnabled && playChatSound) {
        playChatSound('rcvMsg');
      }
      
      // Increment unread count if chat is collapsed
      if (!isExpanded) {
        setUnreadCount(prev => prev + 1);
      }
    };

    const handleChatError = (data: { message: string; gameId: string; rateLimited?: boolean }) => {
      if (data.gameId !== gameId) return;
      setErrorMessage(data.message);
      // Clear error after 5 seconds
      setTimeout(() => setErrorMessage(null), 5000);
    };

    socket.on('chatMessage', handleChatMessage);
    socket.on('chatError', handleChatError);

    return () => {
      if (socket) {
        socket.off('chatMessage', handleChatMessage);
        socket.off('chatError', handleChatError);
      }
    };
  }, [socket, isOnline, gameId, currentPlayerColor, isExpanded, soundEnabled, playChatSound]);

  // Reset messages when game changes
  useEffect(() => {
    setMessages([]);
    setUnreadCount(0);
    setShowMessageMenu(false);
    setShowMobileMessageMenu(false);
  }, [gameId]);

  // Clear unread count when expanding
  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      setUnreadCount(0);
    }
  };

  // On mobile: header opens chat modal (shows messages + quick send button)
  const handleMobileHeaderClick = () => {
    if (isMobile) {
      setShowMobileMessageMenu(true);
      setShowMobileQuickSend(false); // Reset quick send expansion when opening modal
      setUnreadCount(0); // Clear unread count when opening modal
    } else {
      handleToggleExpand();
    }
  };

  // Auto-scroll mobile messages to bottom when new messages arrive
  useEffect(() => {
    if (isMobile && mobileMessagesRef.current && showMobileMessageMenu) {
      mobileMessagesRef.current.scrollTop = mobileMessagesRef.current.scrollHeight;
    }
  }, [messages, isMobile, showMobileMessageMenu]);

  const sendMessage = (message: string) => {
    if (!socket || !gameId || !isOnline || !socket.connected) return;

    // Trim and validate message
    const trimmedMessage = message.trim();
    if (trimmedMessage.length === 0) {
      setErrorMessage('Message cannot be empty');
      setTimeout(() => setErrorMessage(null), 3000);
      return;
    }

    if (trimmedMessage.length > MAX_MESSAGE_LENGTH) {
      setErrorMessage(`Message too long (max ${MAX_MESSAGE_LENGTH} characters)`);
      setTimeout(() => setErrorMessage(null), 3000);
      return;
    }

    socket.emit('sendChatMessage', {
      gameId,
      message: trimmedMessage
    });

    // Play send message sound
    if (soundEnabled && playChatSound) {
      playChatSound('sendMsg');
    }

    // Clear input if it was a typed message
    if (inputMessage) {
      setInputMessage('');
    }

    setShowMessageMenu(false);
    setShowMobileQuickSend(false); // Collapse quick send after sending
    setErrorMessage(null); // Clear any previous errors
    // Don't close modal - keep it open so user can see their message
  };

  const handleInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputMessage.trim().length > 0 && inputMessage.trim().length <= MAX_MESSAGE_LENGTH) {
        sendMessage(inputMessage);
      }
    }
  };

  if (!isOnline || !gameId) {
    return null;
  }

  // Show last 1-2 messages when collapsed (mobile shows only 1 to save space)
  const visibleMessages = isExpanded ? messages : (isMobile ? messages.slice(-1) : messages.slice(-2));

  return (
    <div className={`game-chat-container ${isMobile ? 'mobile' : ''}`}>
      {/* Chat Header - On mobile: opens quick send modal, On desktop: toggles expand */}
      <div className="game-chat-header" onClick={handleMobileHeaderClick}>
        <span className="game-chat-title">💬 Chat</span>
        {unreadCount > 0 && (
          <span className="game-chat-unread-badge">{unreadCount}</span>
        )}
        {/* Hide toggle arrow on mobile - header is now quick send button */}
        {!isMobile && <span className="game-chat-toggle">{isExpanded ? '▼' : '▲'}</span>}
      </div>

      {/* Messages Display - Desktop only (mobile shows messages in modal) */}
      {!isMobile && isExpanded && (
        <div className="game-chat-messages">
          {messages.length === 0 ? (
            <div className="game-chat-empty">No messages yet</div>
          ) : (
            messages.map((msg, index) => (
              <div
                key={index}
                className={`game-chat-message ${msg.isOwn ? 'own' : 'opponent'}`}
              >
                <span className="game-chat-sender">
                  {msg.playerName}:
                </span>
                <span className="game-chat-text">{msg.message}</span>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Collapsed view - desktop shows last 2 messages, mobile shows nothing */}
      {!isExpanded && !isMobile && visibleMessages.length > 0 && (
        <div className="game-chat-messages-collapsed">
          {visibleMessages.map((msg, index) => (
            <div
              key={index}
              className={`game-chat-message ${msg.isOwn ? 'own' : 'opponent'}`}
            >
              <span className="game-chat-sender">
                {msg.playerName}:
              </span>
              <span className="game-chat-text">{msg.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Error Message Display */}
      {errorMessage && (
        <div className="game-chat-error">
          {errorMessage}
        </div>
      )}

      {/* Chat Input/Quick Send - Different for players vs spectators */}
      {!isMobile && (
        <div className="game-chat-send-container" ref={menuRef}>
          {isSpectator ? (
            // Spectators: Quick Send button with preset menu
            <>
              <button
                className="game-chat-send-button"
                onClick={() => setShowMessageMenu(!showMessageMenu)}
              >
                Quick Send {showMessageMenu ? '▲' : '▼'}
              </button>

              {/* Message Menu - Desktop: dropdown */}
              {showMessageMenu && (
                <div className="game-chat-menu">
                  {/* Text Messages */}
                  <div className="game-chat-menu-section">
                    {PREDEFINED_MESSAGES.map((msg, index) => (
                      <button
                        key={index}
                        className="game-chat-menu-item"
                        onClick={() => sendMessage(msg)}
                      >
                        {msg}
                      </button>
                    ))}
                  </div>

                  {/* Emoji Section */}
                  <div className="game-chat-menu-section game-chat-emoji-section">
                    <div className="game-chat-emoji-grid">
                      {CHAT_EMOJIS.map((emoji, index) => (
                        <button
                          key={index}
                          className="game-chat-emoji-button"
                          onClick={() => sendMessage(emoji)}
                          title={emoji}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            // Players: Text input with character counter
            <div className="game-chat-input-container">
              <div className="game-chat-input-wrapper">
                <input
                  ref={inputRef}
                  type="text"
                  className="game-chat-input"
                  placeholder="Type a message..."
                  value={inputMessage}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= MAX_MESSAGE_LENGTH) {
                      setInputMessage(value);
                      setErrorMessage(null);
                    }
                  }}
                  onKeyPress={handleInputKeyPress}
                  maxLength={MAX_MESSAGE_LENGTH}
                />
                <button
                  className="game-chat-send-input-button"
                  onClick={() => sendMessage(inputMessage)}
                  disabled={inputMessage.trim().length === 0 || inputMessage.trim().length > MAX_MESSAGE_LENGTH}
                >
                  Send
                </button>
              </div>
              <div className="game-chat-char-counter">
                {inputMessage.length}/{MAX_MESSAGE_LENGTH}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mobile Full-Screen Message Modal - Shows all messages + input/quick send */}
      {isMobile && showMobileMessageMenu && (
        <div className="game-chat-modal-overlay" onClick={() => setShowMobileMessageMenu(false)}>
          <div className="game-chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="game-chat-modal-header">
              <span className="game-chat-modal-title">💬 Chat</span>
              <button 
                className="game-chat-modal-close-btn"
                onClick={() => {
                  setShowMobileMessageMenu(false);
                  setShowMobileQuickSend(false);
                  setInputMessage('');
                  setErrorMessage(null);
                }}
              >
                ×
              </button>
            </div>
            <div className="game-chat-modal-content">
              {/* Error Message Display */}
              {errorMessage && (
                <div className="game-chat-error">
                  {errorMessage}
                </div>
              )}

              {/* Message History - Scrollable, shows ALL messages */}
              <div className="game-chat-modal-messages" ref={mobileMessagesRef}>
                {messages.length === 0 ? (
                  <div className="game-chat-empty">No messages yet</div>
                ) : (
                  messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`game-chat-message ${msg.isOwn ? 'own' : 'opponent'}`}
                    >
                      <span className="game-chat-sender">
                        {msg.playerName}:
                      </span>
                      <span className="game-chat-text">{msg.message}</span>
                    </div>
                  ))
                )}
              </div>
              
              {/* Input/Quick Send - Different for players vs spectators */}
              {isSpectator ? (
                // Spectators: Quick Send Button - Fixed at bottom
                <div className="game-chat-modal-quick-send-container">
                  <button
                    className="game-chat-modal-quick-send-button"
                    onClick={() => setShowMobileQuickSend(!showMobileQuickSend)}
                  >
                    Quick Send {showMobileQuickSend ? '▲' : '▼'}
                  </button>

                  {/* Expandable Quick Send Section */}
                  {showMobileQuickSend && (
                    <div className="game-chat-modal-quick-send-expanded">
                      {/* Text Messages */}
                      <div className="game-chat-menu-section">
                        {PREDEFINED_MESSAGES.map((msg, index) => (
                          <button
                            key={index}
                            className="game-chat-menu-item"
                            onClick={() => sendMessage(msg)}
                          >
                            {msg}
                          </button>
                        ))}
                      </div>

                      {/* Emoji Section */}
                      <div className="game-chat-menu-section game-chat-emoji-section">
                        <div className="game-chat-emoji-grid">
                          {CHAT_EMOJIS.map((emoji, index) => (
                            <button
                              key={index}
                              className="game-chat-emoji-button"
                              onClick={() => sendMessage(emoji)}
                              title={emoji}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Players: Text input with character counter
                <div className="game-chat-modal-input-container">
                  <div className="game-chat-modal-input-wrapper">
                    <input
                      type="text"
                      className="game-chat-modal-input"
                      placeholder="Type a message..."
                      value={inputMessage}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= MAX_MESSAGE_LENGTH) {
                          setInputMessage(value);
                          setErrorMessage(null);
                        }
                      }}
                      onKeyPress={handleInputKeyPress}
                      maxLength={MAX_MESSAGE_LENGTH}
                    />
                    <button
                      className="game-chat-modal-send-button"
                      onClick={() => sendMessage(inputMessage)}
                      disabled={inputMessage.trim().length === 0 || inputMessage.trim().length > MAX_MESSAGE_LENGTH}
                    >
                      Send
                    </button>
                  </div>
                  <div className="game-chat-modal-char-counter">
                    {inputMessage.length}/{MAX_MESSAGE_LENGTH}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

